import { locales } from './locales.js'

const MINUTE = 60000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export const CLOCK_CHANGE_BANNER_WINDOW = { before: 7 * DAY, after: 3 * DAY }
export const CLOCK_CHANGE_CARD_WINDOW = { before: 14 * DAY, after: 14 * DAY }

const COUNTRIES_BY_STANDARD_OFFSET = {
  0: ['GB', 'IE', 'PT'],
  60: [
    'AD',
    'AT',
    'BE',
    'CH',
    'CZ',
    'DE',
    'DK',
    'ES',
    'FR',
    'HR',
    'HU',
    'IT',
    'LI',
    'LU',
    'MC',
    'MT',
    'NL',
    'NO',
    'PL',
    'SE',
    'SI',
    'SK',
    'SM',
    'VA',
  ],
  120: ['BG', 'CY', 'EE', 'FI', 'GR', 'LT', 'LV', 'RO'],
}

export const COUNTRY_STANDARD_OFFSET = Object.fromEntries(
  Object.entries(COUNTRIES_BY_STANDARD_OFFSET).flatMap(([offset, countries]) =>
    countries.map(country => [country, Number(offset)])
  )
)

const INTL_LOCALE_OVERRIDES = { en: 'en-GB' }

export function lastSundayAt1Utc(year, monthIndex) {
  const date = new Date(Date.UTC(year, monthIndex + 1, 0, 1))
  date.setUTCDate(date.getUTCDate() - date.getUTCDay())
  return date.getTime()
}

export function winterTimeChange(year) {
  return lastSundayAt1Utc(year, 9)
}

export function summerTimeChange(year) {
  return lastSundayAt1Utc(year, 2)
}

export function dayNumber(ms, offsetMinutes) {
  return Math.floor((ms + offsetMinutes * MINUTE) / DAY)
}

export function clockChangeKey(at) {
  return new Date(at).toISOString().slice(0, 10)
}

export function clockChangeCountry({ country, locale }) {
  if (locale === 'ja') return null
  const candidate =
    country || locales.find(item => item.code === locale)?.country || null
  return candidate && candidate in COUNTRY_STANDARD_OFFSET ? candidate : null
}

function bannerPhase(now, at, standardOffset) {
  if (now < at - 48 * HOUR) return 'before'
  if (now < at - 7 * HOUR) return 'countdown'
  if (now < at) return 'tonight'
  const endOfSunday =
    (dayNumber(at, standardOffset) + 1) * DAY - standardOffset * MINUTE
  if (now < endOfSunday) return 'done'
  return 'after'
}

export function clockChangeBanner({
  now,
  country,
  locale,
  enabled = true,
  hidden = null,
}) {
  if (!enabled || !Number.isFinite(now)) return null
  const zoneCountry = clockChangeCountry({ country, locale })
  if (!zoneCountry) return null
  const at = winterTimeChange(new Date(now).getUTCFullYear())
  if (now < at - CLOCK_CHANGE_BANNER_WINDOW.before) return null
  if (now >= at + CLOCK_CHANGE_BANNER_WINDOW.after) return null
  const key = clockChangeKey(at)
  if (hidden === key) return null
  const standardOffset = COUNTRY_STANDARD_OFFSET[zoneCountry]
  const summerOffset = standardOffset + 60
  const from = 1 + summerOffset / 60
  return {
    key,
    at,
    country: zoneCountry,
    from,
    to: from - 1,
    phase: bannerPhase(now, at, standardOffset),
    daysLeft: dayNumber(at, summerOffset) - dayNumber(now, summerOffset),
  }
}

export function clockChangeCard({
  now,
  country,
  locale,
  enabled = true,
  hidden = null,
}) {
  if (!enabled || !Number.isFinite(now)) return null
  const zoneCountry = clockChangeCountry({ country, locale })
  if (!zoneCountry) return null
  const at = winterTimeChange(new Date(now).getUTCFullYear())
  if (now < at - CLOCK_CHANGE_CARD_WINDOW.before) return null
  if (now >= at + CLOCK_CHANGE_CARD_WINDOW.after) return null
  const key = clockChangeKey(at)
  if (hidden === key) return null
  const standardOffset = COUNTRY_STANDARD_OFFSET[zoneCountry]
  const summerOffset = standardOffset + 60
  return {
    key,
    at,
    country: zoneCountry,
    standardOffset,
    summerOffset,
    ...clockChangeHours(standardOffset, 'autumn'),
    phase: now < at ? 'before' : 'after',
    daysLeft: dayNumber(at, summerOffset) - dayNumber(now, summerOffset),
  }
}

export function clockChangeCardFor(state, locale, now = state.clock_now) {
  return clockChangeCard({
    now,
    country: state.clock_country,
    locale,
    enabled: state.clock_change_enabled,
    hidden: state.clock_change_hidden,
  })
}

export function clockChangeBannerFor(state, locale, now = state.clock_now) {
  return clockChangeBanner({
    now,
    country: state.clock_country,
    locale,
    enabled: state.clock_change_enabled,
    hidden: state.clock_change_hidden,
  })
}

export function nextClockChange(now) {
  const year = new Date(now).getUTCFullYear()
  return [
    { season: 'spring', at: summerTimeChange(year) },
    { season: 'autumn', at: winterTimeChange(year) },
    { season: 'spring', at: summerTimeChange(year + 1) },
  ].find(change => change.at > now)
}

export function clockChangeHours(standardOffset, season) {
  const standardHour = 1 + standardOffset / 60
  return season === 'autumn'
    ? { from: standardHour + 1, to: standardHour }
    : { from: standardHour, to: standardHour + 1 }
}

export function guideClockChange({ now, locale }) {
  if (!Number.isFinite(now)) return null
  const country = clockChangeCountry({ country: null, locale })
  if (!country) return null
  const { season, at } = nextClockChange(now)
  const standardOffset = COUNTRY_STANDARD_OFFSET[country]
  const offsetBefore =
    season === 'autumn' ? standardOffset + 60 : standardOffset
  return {
    season,
    at,
    key: clockChangeKey(at),
    ...clockChangeHours(standardOffset, season),
    daysLeft: dayNumber(at, offsetBefore) - dayNumber(now, offsetBefore),
  }
}

export function clockChangeZones(season) {
  return Object.keys(COUNTRIES_BY_STANDARD_OFFSET).map(offset => ({
    standardOffset: Number(offset),
    ...clockChangeHours(Number(offset), season),
  }))
}

export function localShiftHours(at, utcOffsetAt) {
  const before = utcOffsetAt(at - MINUTE)
  const after = utcOffsetAt(at + MINUTE)
  if (before - after !== 60) return null
  const toLocalHour = (ms, offset) =>
    ((Math.floor((ms + offset * MINUTE) / HOUR) % 24) + 24) % 24
  return {
    from: (toLocalHour(at - MINUTE, before) + 1) % 24,
    to: toLocalHour(at + MINUTE, after),
  }
}

export function countdownParts(ms) {
  const total = Math.max(0, Math.floor(ms / 1000))
  return [
    Math.floor(total / 3600),
    Math.floor((total % 3600) / 60),
    total % 60,
  ].map(value => String(value).padStart(2, '0'))
}

export function intlLocale(locale) {
  return (
    INTL_LOCALE_OVERRIDES[locale] ||
    locales.find(item => item.code === locale)?.iso ||
    'fr-FR'
  )
}

export function formatClockHour(locale, hour) {
  if (locale === 'fr') return `${hour} h`
  const padded = String(hour).padStart(2, '0')
  return ['da', 'fi'].includes(locale) ? `${padded}.00` : `${padded}:00`
}

const DATE_STYLES = {
  short: { weekday: 'short', day: 'numeric', month: 'short' },
  long: { weekday: 'long', day: 'numeric', month: 'long' },
  full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  dayOfMonth: { weekday: 'long', day: 'numeric' },
}

export function formatChangeDate(locale, at, style = 'long') {
  const options = DATE_STYLES[style] || DATE_STYLES.long
  return new Intl.DateTimeFormat(intlLocale(locale), {
    ...options,
    timeZone: 'UTC',
  }).format(new Date(at + 11 * HOUR))
}

export function formatDaysLeft(locale, days) {
  if (
    typeof Intl === 'undefined' ||
    typeof Intl.RelativeTimeFormat !== 'function'
  ) {
    return null
  }
  return new Intl.RelativeTimeFormat(intlLocale(locale), {
    numeric: 'auto',
  }).format(days, 'day')
}

export function previewNow(value) {
  const match =
    typeof value === 'string' &&
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?Z$/.exec(value)
  if (!match) return null
  const [year, month, day, hour, minute, second] = match
    .slice(1)
    .map(part => Number(part || 0))
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  if (hour > 23 || minute > 59 || second > 59) return null
  return Date.UTC(year, month - 1, day, hour, minute, second)
}
